import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from 'react-bootstrap/Alert';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import { Checkbox, TextField } from '@mui/material';
import { useAuth } from '../../util/auth';
import { useRouter } from '../../util/router';
import {
  countDataByFullKeys,
  getControlStrategyConfigurationByFullKey,
  getDataByFullKey,
  getIndexValues,
  useGetSummaries
} from '../../util/db';
import CustomTable from '../Table/CustomTable';
import Section from '../Section';
import { archiveRegex } from '../../util/Constant';
import './SPCDashboardSection.scss';
import { getCurrentFeatureFlags } from '../../util/firebase';
import {
  arrayAverage,
  calculateHistogram,
  firebaseClean,
  getSigDigitsUtil,
  getTimeZone
} from '../../util/util';
import { oocFlagColumns, wFlagColumns } from '../Columns';
import { formatInTimeZone } from 'date-fns-tz';
import createTrend from 'trendline';
import SQCLineChart from '../SQCLineChart';
import {
  convertToDailyData,
  extractFullKeys,
  filterChartDataByDateRange,
  formatNumberDisplay,
  getChartLines,
  separateFullKey,
  sortNumberComparator
} from './utils';
import PrintOverviewDataTable from '../Table/PrintOverviewDataTable';
import Loading from '../Common/Loading';
import { Button } from 'react-bootstrap';

const variation = 'spc';

const defaultControlStrategyConfigurations = {
  andersonDarling: 'Off',
  archive: 'No',
  chartSigDigits: 3,
  controlLimitExceedance: 'Out of Control',
  controlLimitViolation: 'Warning',
  controlStrategy: 'Run rules',
  duiChart: 'No',
  ewmaTrend: 'Off',
  fiveConsecutive: 'Off',
  fivePointViolation: 'Off',
  fullKey: '',
  kStdDev: 3,
  lowerControlLimit: 'N/A',
  lowerControlLimitApproach: 'Default',
  lowerToleranceLimit: 'N/A',
  lowerToleranceLimitApproach: 'Default',
  mean: 'N/A',
  n: '',
  standardDeviation: 'N/A',
  nineConsecutive: 'Off',
  nSamplesPerDay: 3,
  pretreatData: 'No',
  repeatability: 'Default',
  reproducibility: 'Default',
  sevenConsecutive: 'Off',
  shortKey: '',
  singlePointViolation: 'Off',
  suppressNormality: 'No',
  suppressTPI: 'No',
  tenNinetyMaterial: 'N/A',
  toleranceLimitExceedance: 'Off',
  tpiOnePointTwo: 'Off',
  twoOfThree: 'Off',
  type: 'Control Strategy',
  upperControlLimit: 'N/A',
  upperControlLimitApproach: 'Default',
  upperToleranceLimit: 'N/A',
  upperToleranceLimitApproach: 'Default',
  warningLimitViolation: 'Off',
  ewmaLambda: '0.4',
  tAlpha: '0.05',
  fAlpha: '0.05'
};

function SPCDashboardSection({ bg, textColor, bgImage, bgImageOpacity }) {
  const auth = useAuth();
  const router = useRouter();
  const { data: rawSummaries } = useGetSummaries(variation);
  const [currentSummaries, setCurrentSummaries] = useState([]);
  const selectedSummaries = useMemo(
    () => currentSummaries.filter((s) => s.select).map((s) => s.fullKey),
    [currentSummaries]
  );
  const [featureFlags] = useState(getCurrentFeatureFlags());
  const [tz] = useState(getTimeZone(featureFlags));
  const [chartElements, setChartElements] = useState([]);
  const [spcFullKeyMap, setSpcFullKeyMap] = useState({});
  const [activeTab, setActiveTab] = useState('Overview');
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Load saved date filters from sessionStorage
  const loadSavedDateFilters = () => {
    try {
      const saved = sessionStorage.getItem('spcChartDateFilters');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Failed to parse saved date filters:', error);
      sessionStorage.removeItem('spcChartDateFilters');
    }
    return { startDate: '', endDate: '' };
  };

  const savedFilters = loadSavedDateFilters();
  const [chartStartDate, setChartStartDate] = useState(savedFilters.startDate);
  const [chartEndDate, setChartEndDate] = useState(savedFilters.endDate);

  const handlePrintClick = () => {
    setActiveTab('PrintOverview');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToOverview = () => {
    setActiveTab('Overview');
  };

  useEffect(() => {
    (async () => {
      const result = await getIndexValues(variation);
      if (!result?.data) return;

      const activeIndexes = result.data.filter((index) => !index.id.includes('Archive'));
      if (!Array.isArray(activeIndexes) || activeIndexes.length === 0) return;

      const allIndexes = activeIndexes[0].index;
      const fullKeys = extractFullKeys(allIndexes);

      const fullKeyLookup = fullKeys.reduce((acc, fullKey) => {
        acc[firebaseClean(fullKey)] = fullKey;
        return acc;
      }, {});
      setSpcFullKeyMap(fullKeyLookup);
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadSummaries = async () => {
      if (!Array.isArray(rawSummaries) || rawSummaries.length === 0) return;
      setLoading(true);

      try {
        const filtered = rawSummaries.filter((s) => !archiveRegex.test(s.id));

        if (filtered.length === 0) {
          if (!cancelled) {
            setCurrentSummaries([]);
            setLoading(false);
          }
          return;
        }

        const fullKeys = filtered.map((s) => spcFullKeyMap[s.id] ?? s.id);

        const counts = await countDataByFullKeys(fullKeys, variation);

        const processed = filtered
          .map((summary) => {
            const fullKey = spcFullKeyMap[summary.id] ?? summary.id;
            const count = counts[fullKey] ?? 0;
            if (count <= 0) return null;

            return {
              ...summary,
              mean_process_or: parseFloat(summary.mean_process_or).toFixed(2),
              r_bar_sd: parseFloat(summary.r_bar_sd).toFixed(2),
              Cp: parseFloat(summary.Cp).toFixed(2),
              Cpk: parseFloat(summary.Cpk).toFixed(2),
              percent_yield: parseFloat(summary.percent_yield).toFixed(2),
              location: fullKey.split('|')[0] || '',
              type: fullKey.split('|')[1] || '',
              product: fullKey.split('|')[2] || '',
              method: fullKey.split('|')[3] || '',
              parameter: fullKey.split('|')[4] || '',
              fullKey,
              select: false
            };
          })
          .filter(Boolean);

        if (!cancelled)
          setCurrentSummaries((prev) => {
            const selectMap = new Map(prev.map((s) => [s.fullKey, s.select]));
            return processed.map((s) => ({
              ...s,
              select: selectMap.get(s.fullKey) || false
            }));
          });
      } catch (error) {
        console.error('Error loading summaries:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSummaries();

    return () => {
      cancelled = true;
    };
  }, [rawSummaries, spcFullKeyMap]);

  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    setCurrentSummaries((prevSummaries) =>
      prevSummaries.map((summary) => ({ ...summary, select: isChecked }))
    );
  };

  const handleRowSelect = (fullKey) => (event) => {
    const isChecked = event.target.checked;
    setCurrentSummaries((prevSummaries) =>
      prevSummaries.map((summary) =>
        summary.fullKey === fullKey ? { ...summary, select: isChecked } : summary
      )
    );
  };

  const detailsColumns = [
    {
      headerName: '',
      field: 'select',
      flex: 0.5,
      minWidth: 50,
      sortable: false,
      renderHeader: () => (
        <Checkbox
          checked={
            selectedSummaries.length === currentSummaries.length && currentSummaries.length > 0
          }
          indeterminate={
            selectedSummaries.length > 0 && selectedSummaries.length < currentSummaries.length
          }
          onChange={handleSelectAll}
        />
      ),
      renderCell: (params) => (
        <Checkbox
          checked={params.row.select || false}
          onChange={handleRowSelect(params.row.fullKey)}
        />
      )
    },
    { headerName: 'Location', field: 'location', flex: 1, minWidth: 230 },
    { headerName: 'Type', field: 'type', flex: 1, minWidth: 170 },
    { headerName: 'Product', field: 'product', flex: 0.5, minWidth: 180 },
    { headerName: 'Method', field: 'method', flex: 1, minWidth: 180 },
    { headerName: 'Parameters', field: 'parameter', flex: 1.5, minWidth: 180 },
    {
      headerName: 'Process Mean',
      field: 'mean_process_or',
      flex: 1,
      minWidth: 70,
      valueFormatter: formatNumberDisplay,
      sortComparator: sortNumberComparator
    },
    {
      headerName: 'Process Range Mean',
      field: 'r_bar_sd',
      flex: 1,
      minWidth: 70,
      valueFormatter: formatNumberDisplay,
      sortComparator: sortNumberComparator
    },
    {
      headerName: 'Process Capability(Cp)',
      field: 'Cp',
      flex: 1,
      minWidth: 70,
      valueFormatter: formatNumberDisplay,
      sortComparator: sortNumberComparator
    },
    {
      headerName: 'Process Capability(Cpk)',
      field: 'Cpk',
      flex: 1,
      minWidth: 70,
      valueFormatter: formatNumberDisplay,
      sortComparator: sortNumberComparator
    },
    {
      headerName: 'Percent Yield',
      field: 'percent_yield',
      flex: 1,
      minWidth: 70,
      valueFormatter: formatNumberDisplay,
      sortComparator: sortNumberComparator
    }
  ];

  const getChartParams = async (fullKey, startDate, endDate) => {
    let currentControlStrategyConfigurationsObj = {};
    const rawChartHits = await getDataByFullKey(fullKey, variation);

    if (!Array.isArray(rawChartHits)) return { chartData: [], chartScalars: {}, chartLines: [] };

    const chartHits = filterChartDataByDateRange(
      rawChartHits,
      startDate || null,
      endDate || null,
      tz
    );

    const controlStrategyConfigurations = await getControlStrategyConfigurationByFullKey(
      fullKey,
      variation
    );
    if (Array.isArray(controlStrategyConfigurations) && controlStrategyConfigurations.length > 0) {
      currentControlStrategyConfigurationsObj = controlStrategyConfigurations.sort((a, b) =>
        b.archive?.localeCompare(a.archive)
      )[0];
    } else {
      currentControlStrategyConfigurationsObj = {
        ...defaultControlStrategyConfigurations,
        fullKey
      };
    }

    const filteredChartHits = chartHits
      .map((cd, index) => {
        const wFlags = wFlagColumns.filter((f) => cd[f]).map((f) => cd[f]);
        const oocFlags = oocFlagColumns.filter((f) => cd[f]).map((f) => cd[f]);
        const flags = [
          wFlags.length > 0 ? `Warnings: ${wFlags.join(', ')}` : '',
          oocFlags.length > 0 ? `OOC: ${oocFlags.join(', ')}` : ''
        ]
          .filter(Boolean)
          .join('; ');

        const row = { index, flags };
        for (const elt of Object.keys(cd)) {
          if (['date'].includes(elt) || ['objectID'].includes(elt)) {
            row[elt] = cd[elt];
          } else if (
            String(cd[elt]).match(/[A-Z]/i) ||
            ['type', 'parameter', 'location', 'product', 'method', 'sampleId'].includes(elt)
          ) {
            row[elt] = cd[elt];
          } else if (!Number.isNaN(parseFloat(cd[elt]))) {
            row[elt] = Number(cd[elt]);
          } else if (elt !== 'flags') {
            row[elt] = cd[elt];
          }
        }
        if (row.dateTime) {
          const timeZone = tz || 'UTC';
          const theDate = new Date(row.dateTime);
          row.date = formatInTimeZone(theDate, timeZone, 'M/d/yyyy');
          row.time = formatInTimeZone(theDate, timeZone, 'h:mm aaa');
        }
        if (row.result) {
          row.result = getSigDigitsUtil(
            row.result,
            currentControlStrategyConfigurationsObj.chartSigDigits
          );
        }
        if (row.resultOmits) {
          row.resultOmits = getSigDigitsUtil(
            row.resultOmits,
            currentControlStrategyConfigurationsObj.chartSigDigits
          );
        }
        return row;
      })
      .sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());

    const newChartData = convertToDailyData(
      filteredChartHits.filter((cd) => !!cd.resultOmits || parseInt(cd.resultOmits, 10) === 0),
      tz,
      currentControlStrategyConfigurationsObj
    );

    const rs = newChartData.map((d) => d.dateAverage);
    const rMin = rs.length > 0 ? Math.min(...rs) : 0;
    const rMax = rs.length > 0 ? Math.max(...rs) : 0;
    const topYMin =
      rMin < newChartData[newChartData.length - 1]?.sd3n
        ? rMin
        : newChartData[newChartData.length - 1]?.sd3n;
    const topYMax =
      rMax > newChartData[newChartData.length - 1]?.sd3p
        ? rMax
        : newChartData[newChartData.length - 1]?.sd3p;
    const topYInterval = topYMax - topYMin;

    const mrMean = arrayAverage(newChartData.map((d) => d.dateRange));
    const dateTrend = newChartData
      .map((item) => ({
        zScoreOmits: Number(item.zScoreOmits),
        resultOmits: Number(item.resultOmits)
      }))
      .filter((item) => !isNaN(item.zScoreOmits) && !isNaN(item.resultOmits));
    let trend;
    try {
      trend = createTrend(dateTrend, 'zScoreOmits', 'resultOmits');
    } catch {
      return { chartData: [], chartScalars: {}, chartLines: [] };
    }
    const zscores = newChartData.map((d) => d.zScoreOmits);
    const newResults = newChartData.map((d) => d.resultOmits);
    const mrs = newChartData
      .filter((d) => !Number.isNaN(parseFloat(d.mrOmits)))
      .map((d) => parseFloat(d.mrOmits));
    const mrMaxVal = mrs.length > 0 ? Math.max(...mrs) : 0;
    const mrUCL = mrMean * 3.27;
    const mrMax = mrUCL > mrMaxVal ? mrUCL : mrMaxVal;
    const xMin = zscores.length > 0 ? Math.min(...zscores) : 0;
    const xMax = zscores.length > 0 ? Math.max(...zscores) : 0;
    const yMin = newResults.length > 0 ? Math.min(...newResults) : 0;
    const yMax = newResults.length > 0 ? Math.max(...newResults) : 0;

    const chartScalars = {
      mrMean,
      mrUCL,
      mrYMax: mrMax * 1.05,
      trendData: [
        { y: trend.calcY(xMin), x: xMin },
        { y: trend.calcY(xMax), x: xMax }
      ],
      qqXMin: xMin,
      qqXMax: xMax,
      qqYMin: yMin,
      qqYMax: yMax,
      cYMin: topYMin - 0.05 * topYInterval,
      cYMax: topYMax + 0.05 * topYInterval,
      histogramData: calculateHistogram(newChartData, yMin, yMax)
    };

    newChartData.forEach((data, index) => {
      data.formattedDate = new Date(data.date);
      data.updatedIndex = index;
      data.userDefinedUTL = currentControlStrategyConfigurationsObj.upperToleranceLimit;
      data.userDefinedLTL = currentControlStrategyConfigurationsObj.lowerToleranceLimit;
      data.userDefinedUCL = currentControlStrategyConfigurationsObj.upperControlLimit;
      data.userDefinedLCL = currentControlStrategyConfigurationsObj.lowerControlLimit;
    });

    const sortedAsc = newChartData.sort((a, b) => Date.parse(a.date) - Date.parse(b.date));
    return {
      currentControlStrategyConfigurationsObj,
      chartData: sortedAsc,
      chartScalars,
      chartLines: getChartLines(sortedAsc.length)
    };
  };

  useEffect(() => {
    const fetchChartData = async () => {
      const elements = await Promise.all(
        selectedSummaries.map(async (fullKey) => {
          const { currentControlStrategyConfigurationsObj, chartData, chartScalars, chartLines } =
            await getChartParams(fullKey, chartStartDate, chartEndDate);
          const { location, method, parameter, product, type } = separateFullKey(fullKey);
          const selectedSummaryInfo = currentSummaries.find(
            (summary) => summary.fullKey === fullKey
          );

          if (
            currentControlStrategyConfigurationsObj &&
            chartData.length > 0 &&
            chartScalars &&
            chartLines.length > 0
          ) {
            return (
              <li key={fullKey} className="info-spc-item">
                <h3 className="info-spc-container">
                  <span className="info-spc-label">Type:</span> {type}
                  <span className="info-spc-label">Product:</span> {product}
                  <span className="info-spc-label">Method:</span> {method}
                  <span className="info-spc-label">Parameter:</span> {parameter}
                </h3>
                <SQCLineChart
                  xAxis="updatedIndex"
                  syncId="chained"
                  yMin={chartScalars.cYMin}
                  yMax={chartScalars.cYMax}
                  data={chartData}
                  lines={chartLines}
                  isEverFlags={false}
                  controlStrategyConfigurations={{
                    currentControlStrategyConfigurations: currentControlStrategyConfigurationsObj
                  }}
                  selectedSummaryInfo={selectedSummaryInfo}
                />
              </li>
            );
          } else {
            return (
              <li key={fullKey} className="info-spc-item">
                <h3 className="info-spc-container">
                  <span className="info-spc-label">Type:</span> {type}
                  <span className="info-spc-label">Product:</span> {product}
                  <span className="info-spc-label">Method:</span> {method}
                  <span className="info-spc-label">Parameter:</span> {parameter}
                </h3>
                <p>Not found data</p>
              </li>
            );
          }
        })
      );

      setChartElements(<ul style={{ width: '100%' }}>{elements}</ul>);
    };

    if (selectedSummaries.length > 0 && Array.isArray(rawSummaries) && rawSummaries.length > 0) {
      fetchChartData();
    } else {
      setChartElements('');
    }
  }, [selectedSummaries, rawSummaries, chartStartDate, chartEndDate]);

  // Save date filters to sessionStorage when they change
  useEffect(() => {
    sessionStorage.setItem(
      'spcChartDateFilters',
      JSON.stringify({ startDate: chartStartDate, endDate: chartEndDate })
    );
  }, [chartStartDate, chartEndDate]);

  const renderSumariesTable = () => {
    if (activeTab === 'PrintOverview') {
      const [init, ...columnPrint] = detailsColumns;
      return (
        <PrintOverviewDataTable
          columns={columnPrint}
          data={currentSummaries}
          tz={tz}
          sx={{
            head: {
              textAlign: 'center',
              verticalAlign: 'middle'
            },
            cell: { paddingRight: 0, paddingLeft: 5, textAlign: 'center', verticalAlign: 'middle' },
            row: { textAlign: 'center', verticalAlign: 'middle' }
          }}
        />
      );
    } else {
      return (
        <CustomTable
          header={detailsColumns}
          data={currentSummaries}
          numberOfRows={10}
          loading={loading}
          sx={{
            '& .MuiDataGrid-cell': {
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitLineClamp: 2,
              whiteSpace: 'normal',
              justifyContent: 'center'
            },
            '& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox': {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 0
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              WebkitLineClamp: 2,

              whiteSpace: 'normal',
              textAlign: 'center'
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              justifyContent: 'center'
            }
          }}
        />
      );
    }
  };
  const renderSumariesBtn = () => {
    if (activeTab === 'PrintOverview') {
      return (
        <div>
          <button
            className="btn btn-primary"
            onClick={handleBackToOverview}
            style={{ marginRight: '10px' }}
          >
            Back
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              window.print();
            }}
          >
            Print
          </button>
        </div>
      );
    } else {
      return (
        <button className="btn btn-primary" onClick={handlePrintClick}>
          Overview Print
        </button>
      );
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setLoading(true);

    try {
      const result = await getIndexValues(variation);
      if (!result?.data) return;

      const activeIndexes = result.data.filter((index) => !index.id.includes('Archive'));
      if (!Array.isArray(activeIndexes) || activeIndexes.length === 0) return;

      const allIndexes = activeIndexes[0].index;
      const fullKeys = extractFullKeys(allIndexes);

      const fullKeyLookup = fullKeys.reduce((acc, fullKey) => {
        acc[firebaseClean(fullKey)] = fullKey;
        return acc;
      }, {});
      setSpcFullKeyMap(fullKeyLookup);

      if (Array.isArray(rawSummaries) && rawSummaries.length > 0) {
        const filtered = rawSummaries.filter((s) => !archiveRegex.test(s.id));
        const fullKeys = filtered.map((s) => fullKeyLookup[s.id] ?? s.id);
        const counts = await countDataByFullKeys(fullKeys, variation);

        const processed = filtered
          .map((summary) => {
            const fullKey = fullKeyLookup[summary.id] ?? summary.id;
            const count = counts[fullKey] ?? 0;
            if (count <= 0) return null;

            return {
              ...summary,
              mean_process_or: parseFloat(summary.mean_process_or).toFixed(2),
              r_bar_sd: parseFloat(summary.r_bar_sd).toFixed(2),
              Cp: parseFloat(summary.Cp).toFixed(2),
              Cpk: parseFloat(summary.Cpk).toFixed(2),
              percent_yield: parseFloat(summary.percent_yield).toFixed(2),
              location: fullKey.split('|')[0] || '',
              type: fullKey.split('|')[1] || '',
              product: fullKey.split('|')[2] || '',
              method: fullKey.split('|')[3] || '',
              parameter: fullKey.split('|')[4] || '',
              fullKey,
              select: false
            };
          })
          .filter(Boolean);

        setCurrentSummaries(processed);
      }
    } catch (error) {
      console.error('Error refreshing summaries:', error);
    } finally {
      setIsRefreshing(false);
      setLoading(false);
    }
  };

  return (
    <Section
      bg={bg}
      textColor={textColor}
      size="md"
      bgImage={bgImage}
      bgImageOpacity={bgImageOpacity}
    >
      {router.query.paid && auth.user.planIsActive && (
        <Alert variant="success" className="text-center mx-auto mb-5" style={{ maxWidth: '400px' }}>
          You are now subscribed to the {auth.user.planId} plan
          <span className="ml-2" role="img" aria-label="party">
            🥳
          </span>
        </Alert>
      )}

      <Row>
        <Col lg={12}>
          <div className="d-flex justify-content-end ">
            <Button
              variant="primary"
              size="sm"
              className="px-4 py-2 d-flex align-items-center gap-2"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                  Refreshing...
                </>
              ) : (
                <>Refresh</>
              )}
            </Button>
          </div>
          {isRefreshing ? (
            <Loading />
          ) : (
            <Tabs
              defaultActiveKey="summaryStatistics"
              id="uncontrolled-tab-example"
              className="tri-tabs"
            >
              <Tab eventKey="summaryStatistics" title="Summary Statistics">
                <div>
                  <div>
                    <div className="header">
                      <h2>Summary Statistics Details</h2>
                    </div>
                    <div className="underline-header">
                      <hr />
                    </div>
                  </div>
                  <div>{renderSumariesTable()}</div>
                  <div className="btn-center">{renderSumariesBtn()}</div>
                </div>
              </Tab>
              <Tab eventKey="charts" title="Charts">
                <div>
                  <div>
                    <div className="header">
                      <h2>Chart Details</h2>
                    </div>
                    <div className="underline-header">
                      <hr />
                    </div>
                  </div>
                  <div className="spc-date-filter-container">
                    <TextField
                      label="Start Date"
                      type="date"
                      size="small"
                      value={chartStartDate}
                      onChange={(e) => setChartStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      data-testid="spc-chart-start-date"
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      size="small"
                      value={chartEndDate}
                      onChange={(e) => setChartEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: chartStartDate || undefined
                      }}
                      data-testid="spc-chart-end-date"
                    />
                    {(chartStartDate || chartEndDate) && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setChartStartDate('');
                          setChartEndDate('');
                        }}
                        data-testid="spc-chart-clear-dates"
                      >
                        Clear Dates
                      </Button>
                    )}
                  </div>
                  <div className="chart-spc-container">
                    {selectedSummaries.length > 0 ? (
                      chartElements
                    ) : (
                      <p>
                        Please select one or more records from the list to display data in the
                        chart.
                      </p>
                    )}
                  </div>
                  <div className="btn-center">
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        window.print();
                      }}
                    >
                      Print
                    </button>
                  </div>
                </div>
              </Tab>
            </Tabs>
          )}
        </Col>
      </Row>
    </Section>
  );
}

SPCDashboardSection.propTypes = {
  bg: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
  bgImage: PropTypes.string.isRequired,
  bgImageOpacity: PropTypes.number.isRequired
};

export default SPCDashboardSection;
